import { getAllElements } from './element.no-deps'
import { isLoopElement } from './typeGuards.no-deps'
import { CachedUnitDataBuilder } from '../units/CachedUnitData.no-deps'
import { findDataAttributeNames } from './findDataAttributeNames.no-deps'
import { applyUnitFiltering } from './UnitFilter'
import { type ContentLoopElementOrder, ContentLoopOrderBy, type Flow } from './types.no-deps'
import type { TFullUnit } from '../units/types.no-deps'
import { map, uniq, filter, sort } from '../../lib/functional/array.no-deps'
import { pipe } from '../../lib/functional/pipe.no-deps'
import type { LoopPlan } from './LoopPlan.no-deps'
import { assumeExhaustion } from '../../lib/typeHelpers.no-deps'
import { partition } from 'lodash'
import { isNil } from '../../lib/functional/nil.no-deps'
import type { WorkflowParameter } from '../workflowParameter/types.no-deps'
import type { UnitWorkflowParameter } from '../UnitWorkflowParameter/types.no-deps'

export type Unit = Pick<TFullUnit, 'id' | 'name' | 'data' | 'parents' | 'isDeleted' | 'type'>
type FetchUnitsForTypes = (unitTypeId: number[]) => Promise<Unit[]>

export function orderUnits(
    units: Unit[],
    order: ContentLoopElementOrder = { orderBy: ContentLoopOrderBy.unitName }
): Unit[] {
    // this check is for backward compatibility, for case where existing flows does not yet have orderBy field set
    if (isNil(order.orderBy)) {
        return orderUnitsByName(units)
    }
    switch (order.orderBy) {
        case ContentLoopOrderBy.unitName:
            return orderUnitsByName(units)

        case ContentLoopOrderBy.attribute:
            return orderUnitsByAttributeWithFallbackToName(units, order.orderByAttribute)

        default:
            assumeExhaustion(order)
    }
}

function orderUnitsByName(units: Unit[]): Unit[] {
    return sort(units, (a, b) => a.name.localeCompare(b.name))
}

function orderUnitsByAttributeWithFallbackToName(units: Unit[], orderByAttribute: string): Unit[] {
    const [withAttribute, withoutAttribute] = partition(units, (unit) => orderByAttribute in unit.data)

    const sortedWithAttribute = orderUnitsByAttribute(withAttribute, orderByAttribute)
    const sortedWithoutAttribute = orderUnitsByName(withoutAttribute)

    return sortedWithAttribute.concat(sortedWithoutAttribute)
}

function orderUnitsByAttribute(units: Unit[], orderByAttribute: string): Unit[] {
    return sort(units, (a, b) => a.data[orderByAttribute].localeCompare(b.data[orderByAttribute]))
}

/**
 * Create a plan of the units to loop over. The plan will be cached with the report
 */
export async function createLoopPlanFromFlow(
    flow: Flow,
    workflowParameters: WorkflowParameter[],
    scheduleParameters: UnitWorkflowParameter[],
    fetchUnitsForTypes: FetchUnitsForTypes,
    reportSubject: Pick<TFullUnit, 'id'> | undefined,
    opts: {
        /** In pessimistic mode, when ambiguous filters are present, we filter out */
        pessimistic?: boolean
    }
) {
    const loopElements = pipe(flow, getAllElements, filter(isLoopElement))

    const allUnits = await pipe(
        loopElements,
        map((e) => e.unitType),
        uniq,
        fetchUnitsForTypes
    )

    const loopPlan: LoopPlan = []
    const cacheBuilder = new CachedUnitDataBuilder()

    for (const element of loopElements) {
        const units = orderUnits(
            allUnits.filter((unit) =>
                applyUnitFiltering(
                    element.unitType,
                    workflowParameters,
                    scheduleParameters,
                    element.unitFilters,
                    reportSubject,
                    unit,
                    {
                        pessimistic: opts.pessimistic,
                    }
                )
            ),
            element
        )
        const attributeNames = findDataAttributeNames(element, { traverseLoops: false })

        // Add to loopPlan
        const loopBreadthIds = units.map((unit) => ({ unitId: unit.id }))
        loopPlan.push({ elementId: element.id, loopBreadthIds })

        // Add the cachedUnitData
        for (const unit of units) {
            for (const attributeName of attributeNames) {
                cacheBuilder.push(unit, attributeName)
            }
        }
    }

    return { loopPlan, cachedUnitDataForLoopPlan: cacheBuilder.build() }
}
